import { z } from 'zod';
import { CODEV_SPECTRAL_RESPONSES } from '../../codevSpectralResponses.js';
import { numberParameter, describeOnce } from './shared.js';

const spectralResponseRow = z.strictObject({ response: z.enum(CODEV_SPECTRAL_RESPONSES) });
const spectralResponseCommon = {
  wavelength_bands: z.number().int().min(1).max(21).default(5)
    .describe('NWL: native wavelength bands and weights, 1–21; the native default is 5 (PromptingGuide p185).'),
};
// RSP and BLA together accept at most ten curves and require at least one.
const spectralResponseSettings = z.union(describeOnce([
  {
    ...spectralResponseCommon,
    responses: z.array(spectralResponseRow).min(1).max(10)
      .describe('Ordered pre-stored response curves to cascade. EYE and PHOTOPIC are aliases. No detector/filter is selected by default.'),
    black_body_kelvin: z.null().default(null),
  },
  {
    ...spectralResponseCommon,
    responses: z.array(spectralResponseRow).max(9).default([]),
    black_body_kelvin: z.number().finite().positive()
      .describe('BLA: explicit blackbody color temperature in kelvin. Null omits the blackbody; no source temperature is selected by default.'),
  },
]).map(shape => z.strictObject(shape)));
export default {
  label: 'Spectral Response', group: 'system',
  description: 'Combined spectral response of a source, detector and filters, with the wavelengths and weights CODE V derives from it.',
  settingsSchema: spectralResponseSettings,
  parameters: [
    { key: 'responses', label: 'Response curves', kind: 'rows', prominent: true, columns: [
      { key: 'response', label: 'Response', kind: 'select', options: CODEV_SPECTRAL_RESPONSES.map(value => ({ value, label: value })) },
    ] },
    numberParameter('black_body_kelvin', 'Blackbody temperature', { unit: 'K', blank: 'No blackbody source', prominent: true }),
    // HIDD_SPEPAN2: NWL "RANGE : 1 to 21".
    numberParameter('wavelength_bands', 'Wavelength bands', { integer: true, range: '1–21' }),
  ],
  coverage: 'Up to ten response curves in total, including an optional blackbody source. The derived weights can depend on the lens, and the lens\'s own wavelengths and weights are not changed. Not supported: custom curves, span overrides and fixed wavelengths. Coating and bulk transmission are separate analyses.',
} as const;
